import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LocaleService } from './locale.service';

const BRAND = 'IMA One';
const DEFAULT_OG_IMAGE = 'https://ai.ima-ems.com/assets/icons/android-chrome-192x192.png';

/** Public marketing paths → i18n keys under `seo.pages.*` */
const PUBLIC_PAGE_SEO: Record<string, { titleKey: string; descriptionKey: string }> = {
  '': { titleKey: 'seo.pages.home.title', descriptionKey: 'seo.pages.home.description' },
  'discover/features': { titleKey: 'seo.pages.features.title', descriptionKey: 'seo.pages.features.description' },
  'discover/solutions': { titleKey: 'seo.pages.solutions.title', descriptionKey: 'seo.pages.solutions.description' },
  'discover/cases': { titleKey: 'seo.pages.cases.title', descriptionKey: 'seo.pages.cases.description' },
  'about': { titleKey: 'seo.pages.about.title', descriptionKey: 'seo.pages.about.description' },
  'service/analysis': { titleKey: 'seo.pages.serviceAnalysis.title', descriptionKey: 'seo.pages.serviceAnalysis.description' },
  'service/audit': { titleKey: 'seo.pages.serviceAudit.title', descriptionKey: 'seo.pages.serviceAudit.description' },
  'service/maintenance': { titleKey: 'seo.pages.serviceMaintenance.title', descriptionKey: 'seo.pages.serviceMaintenance.description' },
  'service/consulting': { titleKey: 'seo.pages.serviceConsulting.title', descriptionKey: 'seo.pages.serviceConsulting.description' },
  'pricing': { titleKey: 'seo.pages.pricing.title', descriptionKey: 'seo.pages.pricing.description' },
  'contact': { titleKey: 'seo.pages.contact.title', descriptionKey: 'seo.pages.contact.description' },
  'contact-us': { titleKey: 'seo.pages.contactUs.title', descriptionKey: 'seo.pages.contactUs.description' },
  'help': { titleKey: 'seo.pages.help.title', descriptionKey: 'seo.pages.help.description' },
  'legal/terms': { titleKey: 'seo.pages.terms.title', descriptionKey: 'seo.pages.terms.description' },
  'legal/privacy': { titleKey: 'seo.pages.privacy.title', descriptionKey: 'seo.pages.privacy.description' },
  'feature/ima-one': { titleKey: 'seo.pages.featureImaOne.title', descriptionKey: 'seo.pages.featureImaOne.description' },
  'feature/imaginus': { titleKey: 'seo.pages.featureImaginus.title', descriptionKey: 'seo.pages.featureImaginus.description' },
  'feature/service': { titleKey: 'seo.pages.featureService.title', descriptionKey: 'seo.pages.featureService.description' },
};

const PRIVATE_PATH_PREFIXES = [
  'home',
  'admin',
  'team/',
  'project/',
  'device/',
  'inventory/',
  'bill-analysis/',
  'customer_service/',
  'places/',
  'groups/',
  'mailbox',
  'task-manager',
  'settings',
  'dashboard/',
  'imaginus',
  'electricity-detail/',
  'plot/',
  'payment-return',
  '404',
];

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly locale = inject(LocaleService);

  syncForCurrentRoute(): void {
    const path = this.normalizePath(this.router.url);
    const isPrivate = this.isPrivatePath(path);
    const pageSeo = !isPrivate ? PUBLIC_PAGE_SEO[path] : undefined;

    const pageTitle = pageSeo
      ? this.t(pageSeo.titleKey, BRAND)
      : BRAND;
    const description = pageSeo
      ? this.t(pageSeo.descriptionKey, this.t('seo.defaultDescription', ''))
      : isPrivate
        ? this.t('seo.appDescription', this.t('seo.defaultDescription', ''))
        : this.t('seo.defaultDescription', '');

    const documentTitle =
      !pageSeo || pageTitle === BRAND ? BRAND : `${pageTitle} | ${BRAND}`;

    this.title.setTitle(documentTitle);
    this.upsertMeta('name', 'description', description);
    this.upsertMeta('property', 'og:title', documentTitle);
    this.upsertMeta('property', 'og:description', description);
    this.upsertMeta('property', 'og:site_name', BRAND);
    this.upsertMeta('property', 'og:type', 'website');
    this.upsertMeta('property', 'og:url', this.buildCanonicalUrl(path));
    this.upsertMeta('property', 'og:locale', this.ogLocale());
    this.upsertMeta('property', 'og:image', DEFAULT_OG_IMAGE);
    this.upsertMeta('name', 'twitter:card', 'summary_large_image');
    this.upsertMeta('name', 'twitter:title', documentTitle);
    this.upsertMeta('name', 'twitter:description', description);
    this.upsertMeta('name', 'twitter:image', DEFAULT_OG_IMAGE);

    if (isPrivate) {
      this.upsertMeta('name', 'robots', 'noindex, nofollow');
    } else {
      this.upsertMeta('name', 'robots', 'index, follow');
    }

    this.upsertLink('canonical', this.buildCanonicalUrl(path));
  }

  private normalizePath(url: string): string {
    let path = (url || '').trim();
    if (path.startsWith('#')) {
      path = path.slice(1);
    }
    if (path.startsWith('/')) {
      path = path.slice(1);
    }
    const q = path.indexOf('?');
    if (q >= 0) {
      path = path.slice(0, q);
    }
    return path;
  }

  private isPrivatePath(path: string): boolean {
    if (!path) {
      return false;
    }
    if (['sign-in', 'register', 'forgot-password', 'login'].includes(path)) {
      return true;
    }
    return PRIVATE_PATH_PREFIXES.some(
      (prefix) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix),
    );
  }

  private buildCanonicalUrl(path: string): string {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://ai.ima-ems.com';
    if (!path) {
      return `${origin}/`;
    }
    return `${origin}/#/${path}`;
  }

  private ogLocale(): string {
    const map: Record<string, string> = {
      'zh-TW': 'zh_TW',
      'zh-CN': 'zh_CN',
      en: 'en_US',
      ja: 'ja_JP',
    };
    return map[this.locale.current] || 'zh_TW';
  }

  private t(key: string, fallback: string): string {
    const translated = this.locale.instant(key);
    return translated !== key ? translated : fallback;
  }

  private upsertMeta(attrSelector: 'name' | 'property', name: string, content: string): void {
    if (!content) {
      return;
    }
    if (this.meta.getTag(`${attrSelector}="${name}"`)) {
      this.meta.updateTag({ [attrSelector]: name, content });
    } else {
      this.meta.addTag({ [attrSelector]: name, content });
    }
  }

  private upsertLink(rel: string, href: string): void {
    if (typeof document === 'undefined' || !href) {
      return;
    }
    let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = href;
  }
}
